import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInfoCircle, FaExclamationTriangle, FaTimesCircle, FaMoneyBillWave } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import adminService from '../../services/adminService';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const Card = styled(motion.div)`
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.99) 0%, rgba(28, 28, 28, 0.99) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.7);
`;

const AlertIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;

  &.info    { color: #3b82f6; }
  &.warning { color: #f59e0b; }
  &.error   { color: #ef4444; }
  &.payment { color: #f97316; }
`;

const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.75rem;
  text-align: center;
`;

const Message = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.65;
  text-align: center;
  margin: 0 0 1.5rem;
`;

const Counter = styled.p`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.35);
  text-align: center;
  margin-bottom: 1rem;
`;

const DismissBtn = styled.button`
  width: 100%;
  padding: 0.75rem;
  border-radius: 10px;
  border: none;
  background: var(--primary, #f59e0b);
  color: #000;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
`;

const ICON_MAP = {
  info:    <FaInfoCircle />,
  warning: <FaExclamationTriangle />,
  error:   <FaTimesCircle />,
  payment: <FaMoneyBillWave />
};

export default function OwnerAlertModal() {
  const { pendingAlerts, setPendingAlerts } = useContext(AuthContext);
  const [dismissing, setDismissing] = useState(false);

  if (!pendingAlerts || pendingAlerts.length === 0) return null;

  const alert = pendingAlerts[0];
  const type = alert.type || 'info';

  const dismiss = async () => {
    setDismissing(true);
    try {
      await adminService.dismissAlert(alert.id);
    } catch {
      // silent — still dismiss from UI
    }
    setPendingAlerts(prev => prev.slice(1));
    setDismissing(false);
  };

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <Card
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
        >
          <AlertIcon className={type}>
            {ICON_MAP[type] || <FaInfoCircle />}
          </AlertIcon>
          <Title>{alert.title}</Title>
          <Message>{alert.message}</Message>
          {pendingAlerts.length > 1 && (
            <Counter>{pendingAlerts.length} notification{pendingAlerts.length > 1 ? 's' : ''} waiting</Counter>
          )}
          <DismissBtn onClick={dismiss} disabled={dismissing}>
            {dismissing ? 'Dismissing…' : 'I Understand'}
          </DismissBtn>
        </Card>
      </Overlay>
    </AnimatePresence>
  );
}
